import { Component } from '@theme/component';
import {
  center,
  closest,
  clamp,
  mediaQueryLarge,
  prefersReducedMotion,
  preventDefault,
  viewTransition,
  scheduler,
} from '@theme/utilities';
import { Scroller, scrollIntoView } from '@theme/scrolling';
import { SlideshowSelectEvent } from '@theme/events';

// The threshold for determining visibility of slides.
const SLIDE_VISIBLITY_THRESHOLD = 0.7;

/* Durée pendant laquelle un défilement de la piste est encore attribué au dernier geste du visiteur
   (doigt levé → élan, molette, clavier, focus). Chaque défilement accepté la prolonge : un long élan
   reste un geste. Au-delà, un défilement de la piste vient du navigateur, pas du visiteur. */
const GESTE_MS = 400;

/* Durée maximale d'un glissement doux commandé par le thème (flèche, pastille, lecture auto, lâcher de
   souris) : pendant ce temps, la piste peut bouger sans geste (le navigateur marque parfois des pauses
   qui font croire à une fin de défilement). */
const COMMANDE_MS = 1500;

/* ═══════════ L'« aimant » du navigateur suit la piste ═══════════
   Safari et Chrome sur iPhone (WebKit) retiennent le cran de la dernière accroche (scroll-snap) faite PAR EUX,
   et y ramènent la piste après une mise en page : quand elle redevient défilable en entrant dans l'écran,
   après une rotation… Les poses faites par le code (pastille, flèche, boucle, arrivée) ne mettent pas ce cran
   à jour : il est donc souvent périmé, et la piste sautait toute seule sur une autre diapositive (la 3e carte
   d'avis). Couper l'aimant le temps d'une mise en page efface ce cran ; rétabli, le navigateur le recalcule
   depuis la position réelle. La piste est remise au pixel près à chaque étape : rien ne bouge à l'écran. */

/**
 * @param {HTMLElement} piste
 * @param {number} x
 */
function remettreX(piste, x) {
  if (Math.abs(piste.scrollLeft - x) <= 0.5) return;
  if (typeof piste.scrollTo === 'function') piste.scrollTo({ left: x, behavior: 'instant' });
  else piste.scrollLeft = x;
}

/**
 * Recale l'aimant de plusieurs pistes à la fois (deux mises en page en tout).
 * @param {Array<[HTMLElement, number]>} liste - [piste, position à garder]
 */
export function recalerAimants(liste) {
  if (!liste.length) return;
  for (const [piste] of liste) piste.setAttribute('data-sans-aimant', '');
  void liste[0][0].offsetWidth;
  for (const [piste, x] of liste) remettreX(piste, x);
  for (const [piste] of liste) piste.removeAttribute('data-sans-aimant');
  void liste[0][0].offsetWidth;
  for (const [piste, x] of liste) remettreX(piste, x);
}

/**
 * La piste d'un diaporama (pas celle d'un diaporama imbriqué).
 * @param {HTMLElement} slideshow
 * @returns {HTMLElement | null}
 */
function pisteDe(slideshow) {
  const ref = /** @type {any} */ (slideshow).refs?.scroller;
  if (ref) return ref;
  for (const el of slideshow.querySelectorAll('slideshow-slides')) {
    if (el.closest('slideshow-component') === slideshow) return /** @type {HTMLElement} */ (el);
  }
  return null;
}

/**
 * Shared viewport observer manager for lazy scroll enablement.
 *
 * Limit the number of compositor layers created by slideshows by only enabling scrolling when the slideshow is in the viewport.
 * Resolves known issues with iOS Safari where too many composition layers will crash the page.
 * When a slideshow is NOT in the viewport, it has overflow: hidden (no compositor layer) — and no snapping
 * (scroll-snap-type: none, snippets/slideshow-styles.liquid): its position cannot drift while it is away.
 * When a slideshow enters the viewport, the [in-viewport] attribute is added, enabling scrolling: snapping
 * stays off until the track is scrollable again at the exact same position, then comes back (recalerAimants).
 */
class SlideshowViewportObserver {
  /** @type {SlideshowViewportObserver | null} */
  static #instance = null;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /**
   * Gets the singleton instance
   * @returns {SlideshowViewportObserver}
   */
  static getInstance() {
    if (!this.#instance) {
      this.#instance = new SlideshowViewportObserver();
    }
    return this.#instance;
  }

  /**
   * Registers a slideshow to be observed for viewport visibility
   * @param {Slideshow} slideshow - The slideshow to observe
   */
  observe(slideshow) {
    if (!this.#observer) {
      this.#observer = new IntersectionObserver(
        (entries) => {
          /** @type {Array<[HTMLElement, number]>} */
          const entrees = [];
          for (const entry of entries) {
            const slideshowElement = /** @type {Slideshow} */ (entry.target);
            if (entry.isIntersecting) {
              if (slideshowElement.hasAttribute('in-viewport')) continue;
              // position de la piste, immobile tant qu'elle était hors de l'écran (ni défilement ni aimant)
              const piste = pisteDe(slideshowElement);
              if (piste) {
                piste.setAttribute('data-sans-aimant', '');
                entrees.push([piste, piste.scrollLeft]);
              }
              slideshowElement.setAttribute('in-viewport', '');
            } else {
              slideshowElement.removeAttribute('in-viewport');
            }
          }
          // pistes de nouveau défilables : même position, aimant recalculé depuis celle-ci
          recalerAimants(entrees);
        },
        {
          rootMargin: '100px',
        }
      );
    }

    this.#observer.observe(slideshow);
  }

  /**
   * Unregisters a slideshow from viewport observation
   * @param {Slideshow} slideshow - The slideshow to unobserve
   */
  unobserve(slideshow) {
    this.#observer?.unobserve(slideshow);
    slideshow.removeAttribute('in-viewport');
  }
}

/**
 * Slideshow custom element that allows sliding between content.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} scroller
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLElement} [current]
 * @property {HTMLElement[]} [thumbnails]
 * @property {HTMLElement[]} [dots]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class Slideshow extends Component {
  static get observedAttributes() {
    return ['initial-slide'];
  }

  /**
   * @param {string} name
   * @param {string} oldValue
   * @param {string} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    // Collection page filtering will Morph slideshow galleries in place, updating
    // the slideshow[initial-slide] and slideshow-slide[hidden] attributes.
    // We need to re-select() the slide after the morph is complete, but not before
    // slideshow-slide elements have their [hidden] attribute updated.
    if (name === 'initial-slide' && oldValue !== newValue) {
      queueMicrotask(() => {
        // Only select if the component is connected and initialized
        if (!this.isConnected || !this.#scroll || !this.refs.slides) return;
        const index = parseInt(newValue, 10) || 0;
        const slide_id = this.refs.slides[index]?.getAttribute('slide-id');
        if (slide_id) {
          this.select({ id: slide_id }, undefined, { animate: false });
        }
      });
    }
  }

  requiredRefs = ['scroller'];

  async connectedCallback() {
    super.connectedCallback();

    // Register with shared viewport observer for lazy scroll enablement.
    // This prevents iOS Safari crashes caused by too many compositor layers.
    SlideshowViewportObserver.getInstance().observe(this);

    // Wait for any in-progress view transitions to finish
    if (viewTransition.current) {
      await viewTransition.current;
      // It's possible that the slideshow was disconnected before the view transition finished
      if (!this.isConnected) return;
    }

    const slideCount = this.slides?.length || 0;
    slideCount <= 1 ? this.#setupSlideshowWithoutControls() : this.#setupSlideshow();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Unregister from shared viewport observer
    SlideshowViewportObserver.getInstance().unobserve(this);

    if (this.#scroll) {
      const { scroller } = this.refs;
      scroller.removeEventListener('mousedown', this.#handleMouseDown);
      this.#scroll.destroy();
    }

    const slideCount = this.slides?.length || 0;
    if (slideCount > 1) {
      this.removeEventListener('mouseenter', this.suspend);
      this.removeEventListener('mouseleave', this.resume);
      this.removeEventListener('pointerenter', this.#handlePointerEnter);
      document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    }

    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
    }

    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
      this.#intersectionObserver = null;
    }

    // Boucle : écouteurs du doigt et minuterie
    if (this.refs.scroller) {
      this.refs.scroller.removeEventListener('touchstart', this.#toucheDebut);
      this.refs.scroller.removeEventListener('touchend', this.#toucheFin);
      this.refs.scroller.removeEventListener('touchcancel', this.#toucheFin);
      document.removeEventListener('touchend', this.#toucheFinAilleurs, { capture: true });
      document.removeEventListener('touchcancel', this.#toucheFinAilleurs, { capture: true });
      document.removeEventListener('touchstart', this.#toucheFinAilleurs, { capture: true });
      this.refs.scroller.removeEventListener('wheel', this.#noterMolette);
    }
    this.removeEventListener('keydown', this.#noterGeste);
    this.removeEventListener('focusin', this.#noterGeste);
    this.removeEventListener('pointerdown', this.#noterGeste);
    clearTimeout(this.#minuterieBoucle);
    clearTimeout(this.#minuterieLargeur);
  }

  /**
   * Morph (filtres de collection, rendu de section) : les copies de la boucle ont pu être retirées.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#boucleMaj();
  }

  /** Indicates whether the slideshow is nested inside another slideshow. */
  get isNested() {
    return this.parentElement?.closest('slideshow-component') !== null;
  }

  get initialSlide() {
    return this.refs.slides?.[this.initialSlideIndex];
  }

  /**
   * Selects a slide based on the input index.
   * @param {number|string|{id: string}} input - The index or id of the slide to select.
   * @param {Event} [event] - The event that triggered the selection.
   * @param {Object} [options] - The options for the selection.
   * @param {boolean} [options.animate=true] - Whether to animate the selection.
   */
  async select(input, event, options = {}) {
    if (this.#disabled || !this.refs.slides?.length) return;
    if (!this.#scroll) return;

    // Store the actual current slide before any mutations
    const currentSlide = this.slides?.[this.current];

    for (const slide of this.refs.slides) {
      if (slide.hasAttribute('reveal')) {
        slide.removeAttribute('reveal');
        slide.setAttribute('aria-hidden', 'true');
      }
    }

    // Boucle naturelle : on glisse vers la vraie diapositive par le plus court chemin (copies comprises).
    // Le jeu de diapositives a pu changer (images de variante masquées) : copies remises à jour d'abord.
    // Une diapositive masquée demandée : retour au mode d'origine.
    if (this.infinite) this.#boucleMaj();
    if (this.#boucle) {
      const reel = this.#boucleIndice(input);
      if (reel === undefined) return;
      if (reel !== null) {
        this.#boucleAller(reel, false, event, options);
        return;
      }
      this.#boucleRetirer(false);
    }

    // Figure out the raw desired index (could be -1 if user is on first slide and clicks prev)
    let requestedIndex = (() => {
      if (typeof input === 'number') return input;
      if (typeof input === 'string') return parseInt(input, 10);
      if ('id' in input) {
        const requestedSlide = this.refs.slides.find((slide) => slide.getAttribute('slide-id') == input.id);

        if (!requestedSlide || !this.slides) return;

        // Force the slide to be revealed if it is hidden
        if (requestedSlide.hasAttribute('hidden')) {
          requestedSlide.setAttribute('reveal', '');
          requestedSlide.setAttribute('aria-hidden', 'false');
        }

        return this.slides.indexOf(requestedSlide);
      }
    })();

    const { current } = this;
    const { slides } = this;

    // Guard checks: no slides, invalid index, or selecting the same slide
    if (!slides?.length || requestedIndex === undefined || isNaN(requestedIndex)) return;

    const requestedSlideElement = slides?.[requestedIndex];
    if (currentSlide === requestedSlideElement) return;

    if (!this.infinite) requestedIndex = clamp(requestedIndex, 0, slides.length - 1);

    event?.preventDefault();

    const { animate = true } = options;
    const lastIndex = slides.length - 1;

    // Decide the actual target index (clamp for infinite loop)
    let index = requestedIndex;
    if (requestedIndex < 0) index = lastIndex;
    else if (requestedIndex > lastIndex) index = 0;

    const isAdjacentSlide = Math.abs(index - current) <= 1 && requestedIndex >= 0 && requestedIndex <= lastIndex;
    const { visibleSlides } = this;
    const instant = prefersReducedMotion() || !animate;

    // If jump is more than 1 or we looped, do the placeholder + reorder trick
    if (!instant && !isAdjacentSlide && visibleSlides.length === 1) {
      this.#disabled = true;
      await this.#scroll.finished; // ensure we're not mid-scroll

      const targetSlide = slides[index];
      if (!targetSlide || !currentSlide) return;

      // Create a placeholder in the original DOM position of targetSlide
      const placeholder = document.createElement('slideshow-slide');
      targetSlide.before(placeholder);

      // Decide whether targetSlide goes before or after currentSlide
      // so that we scroll a short distance in the correct direction
      if (requestedIndex < current) {
        currentSlide.before(targetSlide);
      } else {
        currentSlide.after(targetSlide);
      }

      if (current === 0) this.#scroll.to(currentSlide, { instant: true });

      // Once that scroll finishes, restore the DOM
      queueMicrotask(async () => {
        await this.#scroll.finished;
        this.#disabled = false;

        // Restore the slide back to its original position. This triggers a scroll event.
        placeholder.replaceWith(targetSlide);

        // Instantly scroll to the target slide as its position will have changed
        this.#scroll.to(targetSlide, { instant: true });

        // Force Safari to recalculate the timeline state on timeline refresh (after loop)
        requestAnimationFrame(() => {
          this.setAttribute('refreshing-timeline', '');
          requestAnimationFrame(() => {
            this.removeAttribute('refreshing-timeline');
          });
        });
      });
    }

    const slide = slides[index];
    if (!slide) return;

    const previousIndex = this.current;

    slide.setAttribute('aria-hidden', 'false');

    if (this.#scroll) {
      if (!instant) this.#commandeT = performance.now();
      this.#scroll.to(slide, { instant });
      if (instant) this.#noterRepos();
    }

    this.current = this.slides?.indexOf(slide) || 0;

    this.#centerSelectedThumbnail(index, instant ? 'instant' : 'smooth');

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: event != null,
        trigger: 'select',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
  }

  /**
   * Advances to the next slide.
   * @param {Event} [event] - The event that triggered the next slide.
   * @param {Object} [options] - The options for the next slide.
   * @param {boolean} [options.animate=true] - Whether to animate the next slide.
   */
  next(event, options) {
    event?.preventDefault();
    if (this.infinite) this.#boucleMaj();
    if (this.#boucle) {
      this.#boucleAller(this.#boucleVisibles(), true, event, options);
      return;
    }
    this.select(this.nextIndex, event, options);
  }

  /**
   * Goes back to the previous slide.
   * @param {Event} [event] - The event that triggered the previous slide.
   * @param {Object} [options] - The options for the previous slide.
   * @param {boolean} [options.animate=true] - Whether to animate the previous slide.
   */
  previous(event, options) {
    event?.preventDefault();
    if (this.infinite) this.#boucleMaj();
    if (this.#boucle) {
      this.#boucleAller(-this.#boucleVisibles(), true, event, options);
      return;
    }
    this.select(this.previousIndex, event, options);
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (this.#interval) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;
      // hors de l'écran (ou presque) : on ne tourne pas — la diapositive retrouvée est celle qu'on a quittée
      if (!this.hasAttribute('in-viewport')) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(value) {
    if (value) {
      this.setAttribute('paused', '');
    } else {
      this.removeAttribute('paused');
    }
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  /**
   * The current slide index.
   * @type {number}
   */
  #current = 0;

  get current() {
    return this.#current;
  }

  /**
   * Sets the current slide index and update the DOM
   * @type {number}
   */
  set current(value) {
    const { current, thumbnails, dots, slides, previous, next } = this.refs;

    this.#current = value;

    if (current) current.textContent = `${value + 1}`;

    for (const controls of [thumbnails, dots]) {
      controls?.forEach((el, i) => el.setAttribute('aria-selected', `${i === value}`));
    }

    if (previous) previous.disabled = Boolean(!this.infinite && value === 0);
    if (next) next.disabled = Boolean(!this.infinite && slides && this.nextIndex >= slides.length);
  }

  get infinite() {
    return this.getAttribute('infinite') != null;
  }

  get visibleSlides() {
    return this.#visibleSlides;
  }

  get previousIndex() {
    const { current, visibleSlides } = this;
    const modifier = visibleSlides.length > 1 ? visibleSlides.length : 1;

    return current - modifier;
  }

  get nextIndex() {
    const { current, visibleSlides } = this;
    const modifier = visibleSlides.length > 1 ? visibleSlides.length : 1;

    return current + modifier;
  }

  get atStart() {
    const { current, slides } = this;
    if (this.#boucle) return false;

    return slides?.length ? current === 0 : false;
  }

  get atEnd() {
    const { current, slides } = this;
    if (this.#boucle) return false;

    return slides?.length ? current === slides.length - 1 : false;
  }

  /**
   * Sets the disabled attribute.
   * @param {boolean} value - The value to set the disabled attribute to.
   */
  set disabled(value) {
    this.setAttribute('disabled', String(value));
  }
  /**
   * Whether the slideshow is disabled.
   * @type {boolean}
   */
  get disabled() {
    return (
      this.getAttribute('disabled') === 'true' || (this.hasAttribute('mobile-disabled') && !mediaQueryLarge.matches)
    );
  }

  /**
   * Indicates whether the slideshow is temporarily disabled (e.g., during infinite loop transition).
   * @type {boolean}
   */
  #disabled = false;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /**
   * The Scroller instance that manages scrolling.
   * @type {Scroller}
   */
  #scroll;

  /**
   * The ResizeObserver instance for monitoring scroller size changes
   * @type {ResizeObserver}
   */
  #resizeObserver;

  /**
   * IntersectionObserver for efficient visibility tracking of slides
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Cached visible slides result from IntersectionObserver
   * @type {HTMLElement[]}
   */
  #visibleSlides = [];

  /**
   * Setup the slideshow without controls for zero or one slides
   */
  #setupSlideshowWithoutControls() {
    this.current = 0;
    if (this.hasAttribute('auto-hide-controls')) {
      const { slideshowControls } = this.refs;
      if (slideshowControls instanceof HTMLElement) {
        slideshowControls.hidden = true;
      }
    }

    if (this.refs.slides?.[0]) {
      this.refs.slides[0].setAttribute('aria-hidden', 'false');
    }
  }

  /**
   * Setup the slideshow with controls for when there are multiple slides
   */
  #setupSlideshow() {
    // Setup IntersectionObserver first for efficient visibility tracking
    this.#setupIntersectionObserver();

    // Setup the scroll instance
    const { scroller } = this.refs;
    this.#scroll = new Scroller(scroller, {
      onScroll: this.#handleScroll,
      onScrollStart: this.#onTransitionInit,
      onScrollEnd: this.#onTransitionEnd,
    });

    scroller.addEventListener('mousedown', this.#handleMouseDown);
    // Boucle : pas de replacement tant que le doigt est posé
    scroller.addEventListener('touchstart', this.#toucheDebut, { passive: true });
    scroller.addEventListener('touchend', this.#toucheFin, { passive: true });
    scroller.addEventListener('touchcancel', this.#toucheFin, { passive: true });
    // doigt levé dont la piste n'a pas reçu la fin (diapositive retirée sous le doigt…) : jamais de doigt « collé »
    document.addEventListener('touchend', this.#toucheFinAilleurs, { capture: true, passive: true });
    document.addEventListener('touchcancel', this.#toucheFinAilleurs, { capture: true, passive: true });
    document.addEventListener('touchstart', this.#toucheFinAilleurs, { capture: true, passive: true });
    // Gestes du visiteur (voir #repriseSansGeste) : molette / pavé tactile, clavier, focus, souris
    scroller.addEventListener('wheel', this.#noterMolette, { passive: true });
    this.addEventListener('keydown', this.#noterGeste);
    this.addEventListener('focusin', this.#noterGeste);
    this.addEventListener('pointerdown', this.#noterGeste);

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    this.addEventListener('pointerenter', this.#handlePointerEnter);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.#updateControlsVisibility();

    this.disabled = this.isNested || this.disabled;

    this.resume();

    this.current = this.initialSlideIndex;

    // Batch reads and writes to the DOM
    scheduler.schedule(() => {
      let visibleSlidesAmount = 0;
      const initialSlideId = this.initialSlide?.getAttribute('slide-id');

      // Wait for next frame to ensure layout is fully calculated before setting initial scroll position
      // This prevents race conditions on Safari mobile when section_width is 'full-width'
      requestAnimationFrame(() => {
        if (this.initialSlideIndex !== 0 && initialSlideId) {
          this.select({ id: initialSlideId }, undefined, { animate: false });
          visibleSlidesAmount = 1;
        } else {
          visibleSlidesAmount = this.#updateVisibleSlides();
          if (visibleSlidesAmount === 0) {
            this.select(0, undefined, { animate: false });
            visibleSlidesAmount = 1;
          }
        }
        this.#boucleMaj();
        // Arrivée : toujours la 1re diapositive (ou celle demandée). Avant nos scripts, le navigateur a pu
        // poser la piste ailleurs (Safari / Chrome iPhone : 2e ou 3e carte selon les chargements).
        const initiale = this.slides?.[this.initialSlideIndex] || this.slides?.[0];
        if (!this.#boucle && initiale && !this.#gesteEnCours() && this.#scroll) {
          this.#scroll.to(initiale, { instant: true });
          if (this.#current !== this.initialSlideIndex) this.current = this.initialSlideIndex;
        }
        this.#noterRepos();
      });

      this.#resizeObserver = new ResizeObserver(async () => {
        if (viewTransition.current) await viewTransition.current;

        // Seule la LARGEUR compte pour les copies et la pose : un changement de hauteur (barre d'adresse
        // de l'iPhone, image qui arrive) ne replace jamais la piste.
        this.#largeurMaj();

        if (visibleSlidesAmount > 1) {
          this.#updateVisibleSlides();
        }

        if (this.hasAttribute('auto-hide-controls')) {
          this.#updateControlsVisibility();
        }
      });

      this.#resizeObserver.observe(this.refs.slideshowContainer);
    });
  }

  /**
   * Callback invoked on user initiated scroll to sync the current slide index
   * and emit a slide change event if the index has changed.
   */
  #handleScroll = () => {
    // Largeur changée, observateur de taille pas encore passé : ce défilement vient de la nouvelle mise en
    // page. Ni remise en place (la position de repos est celle de l'ancienne largeur : ce serait une autre
    // diapositive), ni adoption : #reposer s'en charge, sur la diapositive d'avant le changement.
    if (this.#largeurEnAttente()) return;

    if (this.#repos !== null && !this.#defilementVoulu()) {
      // la piste a bougé sans geste du visiteur ni commande du thème : remise en place, rien n'est adopté
      if (this.#repriseSansGeste()) return;
      // piste à sa position de repos : rien de nouveau (surtout pas une diapositive recalculée d'après une
      // autre mise en page, ex. toutes les cartes visibles en paysage)
      if (Math.abs(this.refs.scroller.scrollLeft - this.#repos) <= 1) return;
      // garde-fou atteint (le navigateur insiste) : on s'aligne sur ce qu'il montre
    }

    const previousIndex = this.#current;
    const index = this.#sync();

    if (index === previousIndex) return;

    const slide = this.slides?.[index];
    if (!slide) return;

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: true,
        trigger: 'scroll',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
  };

  #onTransitionInit = () => {
    this.setAttribute('transitioning', '');
  };

  #onTransitionEnd = () => {
    this.#updateVisibleSlides();
    this.removeAttribute('transitioning');
    // nouvelle largeur pas encore traitée : #reposer posera la piste
    if (this.#largeurEnAttente()) return;
    this.#boucleRecentrer();
    // la piste s'est posée (geste, flèche, pastille, ou reprise) : c'est la nouvelle position de repos
    this.#noterRepos();
  };

  /* ═══════════ La piste ne bouge que sous la main du visiteur ═══════════
     Safari et Chrome sur iPhone (WebKit) « ré-aimantent » parfois la piste d'eux-mêmes sur un cran
     mémorisé et périmé : à l'entrée dans l'écran, après une mise en page… (constaté : la 3e carte d'avis
     qui s'affiche seule). Horizon adoptait ce défilement comme un choix du visiteur, et la boucle le
     confirmait. Désormais, un défilement de la piste sans geste du visiteur (doigt, souris, molette,
     clavier, focus) ni glissement commandé par le thème (flèche, pastille, lecture auto) est annulé :
     la piste est remise instantanément, à l'HORIZONTALE seulement, là où elle était. Jamais la page. */

  /** Dernier signe d'un geste du visiteur sur ce carrousel (performance.now()). */
  #gesteT = -Infinity;

  /** Position de repos de la piste (scrollLeft) : là où le visiteur ou le thème l'a laissée. */
  /** @type {number | null} */
  #repos = null;

  /** Largeur du conteneur au dernier passage de l'observateur de taille (-1 : jamais mesurée). */
  #largeurVue = -1;

  /** Début du dernier glissement doux commandé par le thème (voir COMMANDE_MS). */
  #commandeT = -Infinity;

  /** Instants des dernières remises en place (garde-fou : jamais de bras de fer avec le navigateur). */
  /** @type {number[]} */
  #reprises = [];

  #noterGeste = () => {
    this.#gesteT = performance.now();
  };

  /** Molette ou pavé tactile : seulement un mouvement de côté (la page qui défile sous la souris n'en est pas un). */
  #noterMolette = (/** @type {WheelEvent} */ e) => {
    if (e.shiftKey || Math.abs(e.deltaX) > Math.abs(e.deltaY)) this.#noterGeste();
  };

  /** Un geste du visiteur est-il en cours (doigt posé, souris qui tire, élan qui continue) ? */
  #gesteEnCours() {
    return this.#doigt || this.#dragging || performance.now() - this.#gesteT < GESTE_MS;
  }

  /** Dernière position (et largeur) où l'aimant du navigateur a été recalé (voir recalerAimants). */
  #aimantX = NaN;
  #aimantL = NaN;

  #noterRepos() {
    const { scroller } = this.refs;
    if (!scroller) return;
    this.#repos = scroller.scrollLeft;
    // l'aimant du navigateur suit chaque pose : à l'écran, ni doigt ni souris sur la piste, pas en plein glissement
    if (!this.hasAttribute('in-viewport') || this.#doigt || this.#dragging || this.hasAttribute('transitioning')) return;
    const largeur = scroller.clientWidth;
    if (Math.abs(this.#repos - this.#aimantX) <= 0.5 && largeur === this.#aimantL) return;
    this.#aimantX = this.#repos;
    this.#aimantL = largeur;
    recalerAimants([[scroller, this.#repos]]);
  }

  /**
   * Le défilement en cours vient-il du visiteur (geste, élan) ou d'un glissement commandé par le thème
   * (flèche, pastille, lecture auto, réordonnancement) ? Sinon, il vient du navigateur seul.
   * @returns {boolean}
   */
  #defilementVoulu() {
    const maintenant = performance.now();
    if (this.#gesteEnCours()) {
      // l'élan d'un geste prolonge le geste
      this.#gesteT = maintenant;
      return true;
    }
    return this.#anime || this.#disabled || maintenant - this.#commandeT < COMMANDE_MS;
  }

  /**
   * Défilement du navigateur seul : la piste est remise à sa position de repos (instantanément, à
   * l'horizontale seulement).
   * @returns {boolean} true si la piste vient d'être remise en place
   */
  #repriseSansGeste() {
    const { scroller } = this.refs;
    if (!scroller || !this.#scroll || this.#repos === null) return false;
    if (Math.abs(scroller.scrollLeft - this.#repos) <= 1) return false;
    const maintenant = performance.now();
    // garde-fou : au plus 4 remises en place par tranche de 3 s ; au-delà, on laisse faire le navigateur
    this.#reprises = this.#reprises.filter((t) => maintenant - t < 3000);
    if (this.#reprises.length >= 4) return false;
    this.#reprises.push(maintenant);
    this.#scroll.to(this.#repos, { instant: true });
    return true;
  }

  /* ═══════════ Largeur qui change (rotation d'écran, fenêtre, colonne) ═══════════
     La position de repos (scrollLeft) ne vaut que pour une largeur : en paysage, 4 560 px n'est plus la
     1re carte. Entre la nouvelle mise en page et le passage de l'observateur de taille, le navigateur
     recale la piste comme il peut (Chrome : même cran ; Safari : cran mémorisé, parfois périmé) : ces
     défilements ne sont ni remis ni adoptés. Puis la piste est reposée sur la diapositive d'AVANT le
     changement, copies refaites si le pas a changé. */

  /** @type {number | undefined} */
  #minuterieLargeur = undefined;

  /**
   * @returns {boolean} true si la largeur du conteneur a changé depuis le dernier passage de l'observateur
   */
  #largeurEnAttente() {
    const conteneur = this.refs.slideshowContainer;
    if (this.#largeurVue < 0 || !conteneur || conteneur.clientWidth === this.#largeurVue) return false;
    // l'observateur passe d'ordinaire dans la même image ; filet de sécurité s'il ne venait pas
    clearTimeout(this.#minuterieLargeur);
    this.#minuterieLargeur = setTimeout(this.#largeurMaj, 200);
    return true;
  }

  /** Largeur du conteneur changée depuis le dernier passage ? Même diapositive, copies refaites au besoin. */
  #largeurMaj = () => {
    clearTimeout(this.#minuterieLargeur);
    const largeur = this.refs.slideshowContainer ? this.refs.slideshowContainer.clientWidth : 0;
    if (largeur === this.#largeurVue) return;
    const premiere = this.#largeurVue < 0;
    this.#largeurVue = largeur;
    if (premiere) this.#boucleMaj();
    else this.#reposer(largeur);
  };

  /**
   * Nouvelle largeur : la piste revient sur la diapositive d'avant le changement (#current : aucun
   * défilement sans geste ne l'a modifié entre-temps).
   * @param {number} largeur
   */
  #reposer(largeur) {
    const { scroller } = this.refs;
    if (!scroller || !this.#scroll) return;
    // doigt posé ou souris qui tire : le visiteur mène ; piste masquée, désactivée ou verticale : rien à poser
    if (!largeur || this.#doigt || this.#dragging || this.disabled || this.#scroll.axis !== 'x') {
      this.#boucleMaj();
      this.#noterRepos();
      return;
    }
    const n = (this.slides || []).length;
    const reel = Math.min(Math.max(0, this.#current), Math.max(0, n - 1));
    this.#anime = false;
    this.#boucleMaj(reel);
    const cible = (this.slides || [])[reel];
    if (cible) {
      if (this.#boucle) this.#vpos = reel;
      this.#scroll.to(cible, { instant: true });
    }
    this.#noterRepos();
  }

  /**
   * Synchronizes the scroll position and updates the current slide index.
   * @returns {number} The index of the current slide.
   */
  #sync = () => {
    const { slides } = this;
    if (!slides) return (this.current = 0);

    if (!this.#scroll) return (this.current = 0);

    // Boucle : la diapositive (vraie ou copie) posée au début de la piste
    if (this.#boucle) {
      const proche = this.#boucleProche();
      if (!proche) return this.current;
      this.#vpos = proche.v;
      return (this.current = this.#mod(proche.v));
    }

    const visibleSlides = this.visibleSlides;

    if (!visibleSlides.length) return this.current;

    const { axis } = this.#scroll;
    const { scroller } = this.refs;
    const centers = visibleSlides.map((slide) => center(slide, axis));
    const referencePoint = visibleSlides.length > 1 ? scroller.getBoundingClientRect()[axis] : center(scroller, axis);
    const closestCenter = closest(centers, referencePoint);
    const closestVisibleSlide = visibleSlides[centers.indexOf(closestCenter)];

    if (!closestVisibleSlide) return (this.current = 0);

    const index = slides.indexOf(closestVisibleSlide);

    return (this.current = index);
  };

  #dragging = false;

  /**
   * Handles the 'mousedown' event to start dragging slides.
   * @param {MouseEvent} event - The mousedown event.
   */
  #handleMouseDown = (event) => {
    const { slides } = this;

    if (!slides || slides.length <= 1) return;
    if (!(event.target instanceof Element)) return;
    if (this.disabled || this.#dragging) return;

    // Check if the event target is within a 3D model interactive element
    // This prevents the slideshow from capturing drag events when interacting with 3D models
    if (event.target.closest('model-viewer')) {
      return;
    }

    event.preventDefault();
    // Store initial position but don't start handling yet
    const { axis } = this.#scroll;
    const startPosition = event[axis];

    const controller = new AbortController();
    const { signal } = controller;
    const startTime = performance.now();
    let previous = startPosition;
    let velocity = 0;
    let moved = false;
    let distanceTravelled = 0;

    this.#dragging = true;
    this.#anime = false;

    /**
     * Handles the 'pointermove' event to update the scroll position.
     * @param {PointerEvent} event - The pointermove event.
     */
    const onPointerMove = (event) => {
      const current = event[axis];
      const initialDelta = startPosition - current;

      if (!initialDelta) return;

      if (!moved) {
        moved = true;
        this.setPointerCapture(event.pointerId);

        // Prevent clicks once the user starts dragging
        document.addEventListener('click', preventDefault, { once: true, signal });

        const movingRight = initialDelta < 0;
        const movingLeft = initialDelta > 0;

        // Check if the current slideshow should handle this drag
        const closestSlideshow = this.parentElement?.closest('slideshow-component');
        const isNested = closestSlideshow instanceof Slideshow && closestSlideshow !== this;
        const cannotMoveInDirection = (movingRight && this.atStart) || (movingLeft && this.atEnd);

        // Abort and let the parent slideshow handle the drag if we're moving in a direction where nested slideshow can't move
        if (isNested && cannotMoveInDirection) {
          controller.abort();
          return;
        }

        this.pause();
        this.setAttribute('dragging', '');
      }

      // Stop the event from bubbling up to parent slideshow components
      event.stopImmediatePropagation();

      const delta = previous - current;
      const timeDelta = performance.now() - startTime;
      velocity = Math.round((delta / timeDelta) * 1000);
      previous = current;
      distanceTravelled += Math.abs(delta);

      this.#scroll.by(delta, { instant: true });

      // Boucle : la souris peut tirer sans fin, on se replace d'un tour (même image) en passant sur les copies
      if (this.#boucle) {
        const proche = this.#boucleProche();
        const n = this.#boucle.n;
        if (proche && (proche.v < 0 || proche.v > n - 1)) {
          const decal = this.#mod(proche.v) - proche.v;
          const jumeau = this.#boucleEl(proche.v + decal);
          if (jumeau) {
            this.#boucleSauter(jumeau.getBoundingClientRect().left - proche.el.getBoundingClientRect().left);
            this.#vpos += decal;
          }
        }
      }
    };

    /**
     * Handles the 'pointerup' event to stop dragging slides.
     * @param {PointerEvent} event - The pointerup event.
     */
    const onPointerUp = async (event) => {
      controller.abort();
      const { current, slides } = this;
      const { scroller } = this.refs;

      this.#dragging = false;

      if (!slides?.length || !scroller) return;

      const direction = Math.sign(velocity);
      const vAvant = this.#vpos;
      const next = this.#sync();

      const modifier = current !== next || Math.abs(velocity) < 10 || distanceTravelled < 10 ? 0 : direction;
      let newIndex = clamp(next + modifier, 0, slides.length - 1);
      /** @type {HTMLElement | null} */
      let cibleBoucle = null;

      // Boucle : même règle, mais en positions virtuelles (on peut finir sur une copie, replacée ensuite)
      if (this.#boucle) {
        const b = this.#boucle;
        const modif = vAvant !== this.#vpos || Math.abs(velocity) < 10 || distanceTravelled < 10 ? 0 : direction;
        const v = clamp(this.#vpos + modif, -b.k, b.n + b.k - b.vues);
        cibleBoucle = this.#boucleEl(v);
        if (cibleBoucle) {
          this.#vpos = v;
          this.#anime = true;
          this.#animeT = performance.now();
          newIndex = this.#mod(v);
        }
      }

      const newSlide = slides[newIndex];
      const currentIndex = this.current;

      if (!newSlide) throw new Error(`Slide not found at index ${newIndex}`);

      this.#commandeT = performance.now();
      this.#scroll.to(cibleBoucle || newSlide);

      this.removeAttribute('dragging');
      this.releasePointerCapture(event.pointerId);

      this.#centerSelectedThumbnail(newIndex);

      this.dispatchEvent(
        new SlideshowSelectEvent({
          index: newIndex,
          previousIndex: currentIndex,
          userInitiated: true,
          trigger: 'drag',
          slide: newSlide,
          id: newSlide.getAttribute('slide-id'),
        })
      );

      this.current = newIndex;

      await this.#scroll.finished;

      // It's possible that the user started dragging again before the scroll finished
      if (this.#dragging) return;

      this.#scroll.snap = true;
      this.resume();
    };

    this.#scroll.snap = false;

    document.addEventListener('pointermove', onPointerMove, { signal });
    document.addEventListener('pointerup', onPointerUp, { signal });
    /**
     * pointerDown calls onPointerUp to fix an issue where the first tap-and-drag
     * on the zoom dialog is captured by the pointerMove/pointerUp listeners,
     * sometimes causing the slideshow to change slides unexpectedly
     */
    document.addEventListener('pointerdown', onPointerUp, { signal });
    document.addEventListener('pointercancel', onPointerUp, { signal });
    document.addEventListener('pointercapturelost', onPointerUp, { signal });
  };

  #handlePointerEnter = () => {
    this.setAttribute('actioned', '');
  };

  /* ═══════════ Boucle naturelle (attribut [infinite]) ═══════════
     Quand les diapositives dépassent de la piste, des COPIES des dernières sont posées en tête et des
     premières en queue. On glisse donc dans les deux sens sans jamais buter ni rembobiner : de la
     1re, un glissement vers la droite montre la dernière ; de la dernière, vers la gauche, la 1re.
     Une fois la piste posée sur une copie, on se replace d'un tour complet, instantanément, sur la
     vraie diapositive : c'est la même image au pixel près, rien ne bouge à l'écran.
     Pastilles, compteur, miniatures et événements parlent toujours de la VRAIE diapositive. Les copies
     sont muettes (aria-hidden, hors tabulation, sans ref ni id). Sans [infinite], ou quand tout tient à
     l'écran, rien n'est ajouté : comportement d'origine. */

  /** @type {{ n: number, k: number, vis: number, vues: number, cle: string, tete: HTMLElement[], queue: HTMLElement[] } | null} */
  #boucle = null;

  /** Position visée, en diapositives « virtuelles » : -k … n+k-1 (0 … n-1 = les vraies). */
  #vpos = 0;

  /** Doigt posé sur la piste. */
  #doigt = false;

  /** Un glissement commandé (flèche, pastille, lecture auto) est en route vers #vpos, depuis #animeT. */
  #anime = false;
  #animeT = 0;

  /** Aimantation coupée le temps d'un saut en plein glissement (sinon le navigateur ré-aimante le saut). */
  #aimantCoupe = false;

  /** @type {number | undefined} */
  #minuterieBoucle = undefined;

  /** Début de l'attente d'une piste pas encore posée (voir #boucleRecentrer), 0 sinon. */
  #recentrerT = 0;

  #toucheDebut = () => {
    this.#doigt = true;
    this.#gesteT = performance.now();
    this.#anime = false;
    this.#recentrerT = 0;
    clearTimeout(this.#minuterieBoucle);
    // Doigt reposé avant la fin de l'élan (glissés enchaînés) : on se replace TOUT DE SUITE sur la vraie
    // diapositive si le navigateur le permet (Chrome finit parfois son élan avant de céder au doigt : le
    // saut est alors ignoré, sans effet visible ; c'est la réserve de copies qui évite la butée).
    this.#boucleRecentrerDoigt();
  };

  /** Au contact du doigt : piste sur une copie → même image, un tour plus loin (aimantation coupée le temps du saut). */
  #boucleRecentrerDoigt() {
    const b = this.#boucle;
    const { scroller } = this.refs;
    if (!b || !scroller || !this.#scroll || this.#dragging) return;
    const proche = this.#boucleProche();
    if (!proche) return;
    const decal = this.#mod(proche.v) - proche.v;
    if (decal) {
      const jumeau = this.#boucleEl(proche.v + decal);
      if (jumeau) {
        // sans cela, le navigateur ré-aimanterait le saut fait en plein élan (l'image bougerait)
        this.#scroll.snap = false;
        this.#boucleSauter(jumeau.getBoundingClientRect().left - proche.el.getBoundingClientRect().left);
        this.#scroll.snap = true;
        this.#aimantCoupe = false;
      }
    }
    this.#vpos = proche.v + decal;
  }

  /** Fin de toucher vue par la page : si la piste ne l'a pas reçue alors qu'aucun doigt ne la touche plus. */
  #toucheFinAilleurs = (/** @type {TouchEvent} */ e) => {
    if (!this.#doigt) return;
    const { scroller } = this.refs;
    if (e.type === 'touchstart' ? scroller && scroller.contains(/** @type {Node} */ (e.target)) : e.touches && e.touches.length) return;
    // la piste reçoit sa propre fin juste après (phase de bulle) : on la laisse faire
    if (e.type !== 'touchstart' && scroller && scroller.contains(/** @type {Node} */ (e.target))) return;
    this.#toucheFin();
  };

  #toucheFin = () => {
    this.#doigt = false;
    // doigt levé : l'élan qui suit fait partie du geste
    this.#gesteT = performance.now();
    clearTimeout(this.#minuterieBoucle);
    // si la piste ne défile plus (pas d'élan), personne d'autre ne replacera : on s'en charge
    this.#minuterieBoucle = setTimeout(this.#boucleRecentrer, 120);
  };

  /**
   * @param {number} v
   * @returns {number} l'indice réel (0 … n-1) d'une position virtuelle
   */
  #mod(v) {
    const n = this.#boucle ? this.#boucle.n : (this.slides || []).length || 1;
    return ((v % n) + n) % n;
  }

  /**
   * @param {number} v - position virtuelle
   * @returns {HTMLElement | null} la diapositive (vraie ou copie) à cette position
   */
  #boucleEl(v) {
    const b = this.#boucle;
    const slides = this.slides;
    if (!b || !slides) return null;
    if (v < 0) return b.tete[b.k + v] || null;
    if (v >= b.n) return b.queue[v - b.n] || null;
    return slides[v] || null;
  }

  /** Marges intérieures de la piste (gouttières) et bord de référence de l'accroche. */
  #boucleCadre() {
    const { scroller } = this.refs;
    const cs = getComputedStyle(scroller);
    const r = scroller.getBoundingClientRect();
    const gauche = r.left + (parseFloat(cs.paddingLeft) || 0);
    const droite = r.right - (parseFloat(cs.paddingRight) || 0);
    return { rtl: cs.direction === 'rtl', gauche, droite };
  }

  /** La boucle a-t-elle lieu d'être ? [infinite], actif, horizontal, et les vraies diapositives dépassent. */
  #boucleVoulue() {
    if (!this.infinite || this.disabled || this.isNested) return false;
    const slides = this.slides;
    const { scroller } = this.refs;
    if (!slides || slides.length < 2 || !scroller || !scroller.clientWidth) return false;
    if (getComputedStyle(scroller).flexDirection.indexOf('column') === 0) return false;
    const { gauche, droite } = this.#boucleCadre();
    let min = Infinity;
    let max = -Infinity;
    for (const slide of slides) {
      const r = slide.getBoundingClientRect();
      if (!r.width) return false;
      if (r.left < min) min = r.left;
      if (r.right > max) max = r.right;
    }
    return max - min > droite - gauche + 1;
  }

  /**
   * Ajoute, reconstruit ou retire les copies selon l'état courant (appelé à la mise en place, au
   * redimensionnement et après un morph).
   */
  #boucleMaj = (/** @type {number | undefined} */ garder) => {
    const { scroller } = this.refs;
    if (!scroller || !this.#scroll) return;
    const b = this.#boucle;
    const voulue = this.#boucleVoulue();
    const slides = this.slides || [];
    const n = slides.length;
    let k = 0;
    let vis = 1;
    let vues = 1;
    if (voulue) {
      const r0 = slides[0].getBoundingClientRect();
      const pas = Math.abs(slides[1].getBoundingClientRect().left - r0.left) || r0.width || 1;
      const { gauche, droite } = this.#boucleCadre();
      const large = droite - gauche;
      // pas des flèches : les diapositives visibles à 70 % une fois posées (comme Horizon), mesuré au repos
      vis = Math.max(1, Math.min(n - 1, Math.floor((large - SLIDE_VISIBLITY_THRESHOLD * r0.width) / pas) + 1));
      // diapositives au moins en partie à l'écran (aperçu compris) : la vue ne doit jamais manquer de contenu
      vues = Math.max(1, Math.ceil((large - 1) / pas));
      // copies de chaque côté : de quoi enchaîner plusieurs glissés forts dans le même sens sans jamais
      // buter, même si l'élan ne laisse pas le temps de se replacer (au-delà de n, les copies reprennent
      // le cycle : 1, 2, 3, 1, 2, 3…)
      k = Math.max(2 * vues + 1, Math.min(4 * n, 12));
    }
    // la clé porte aussi le pas (largeur + écart) : s'il change, copies et pose sont refaites
    const pasCle = voulue ? Math.round(Math.abs(slides[1].getBoundingClientRect().left - slides[0].getBoundingClientRect().left)) : 0;
    const cle = voulue ? `${n}:${k}:${vis}:${pasCle}:${slides.map((s) => s.getAttribute('slide-id') || '').join(',')}` : '';
    const intactes = Boolean(
      b && b.tete.length === b.k && b.tete.concat(b.queue).every((c) => c.parentNode === scroller)
    );
    if (b && voulue && b.cle === cle && intactes) return;
    if (!b && !voulue && !scroller.querySelector(':scope > [data-boucle-clone]')) return;

    // la diapositive réellement à l'écran, pour s'y retrouver après (ou celle imposée : changement de
    // largeur, où ce que montre la piste vient de la nouvelle mise en page)
    let reel = Math.min(this.#current, Math.max(0, n - 1));
    if (typeof garder === 'number') {
      reel = Math.min(Math.max(0, garder), Math.max(0, n - 1));
    } else if (b && intactes && b.n === n) {
      const proche = this.#boucleProche();
      if (proche) reel = this.#mod(proche.v);
    }

    this.#boucleRetirer(false);

    if (voulue) {
      const tete = [];
      const queue = [];
      const cycle = (/** @type {number} */ v) => ((v % n) + n) % n;
      for (let v = -k; v < 0; v++) tete.push(this.#boucleCopie(slides[cycle(v)], v, cycle(v), n));
      for (let v = n; v < n + k; v++) queue.push(this.#boucleCopie(slides[cycle(v)], v, cycle(v), n));
      slides[0].before(...tete);
      slides[n - 1].after(...queue);
      this.#boucle = { n, k, vis, vues, cle, tete, queue };
    }

    const cible = this.slides && this.slides[reel];
    if (cible) {
      this.#vpos = reel;
      this.#scroll.to(cible, { instant: true });
      if (reel !== this.#current) this.current = reel;
    }
    this.#noterRepos();
  };

  /**
   * Retire toutes les copies.
   * @param {boolean} [recadrer=true] - se replacer sur la vraie diapositive visible
   */
  #boucleRetirer(recadrer = true) {
    const { scroller } = this.refs;
    const reel = this.#boucle ? this.#mod(this.#vpos) : this.#current;
    const copies = scroller ? scroller.querySelectorAll(':scope > [data-boucle-clone]') : [];
    for (const copie of copies) copie.remove();
    const avait = this.#boucle !== null || copies.length > 0;
    this.#boucle = null;
    const cible = this.slides && this.slides[reel];
    if (recadrer && avait && cible && this.#scroll) {
      this.#scroll.to(cible, { instant: true });
      this.current = reel;
      this.#noterRepos();
    }
  }

  /**
   * Copie muette d'une diapositive : même rendu, mais ni ref, ni id, ni attribut d'éditeur, hors tabulation.
   * @param {HTMLElement} slide
   * @param {number} v - position virtuelle de la copie
   * @param {number} reel - indice de la vraie diapositive
   * @param {number} n - nombre de vraies diapositives
   * @returns {HTMLElement}
   */
  #boucleCopie(slide, v, reel, n) {
    const copie = /** @type {HTMLElement} */ (slide.cloneNode(true));
    for (const nom of ['ref', 'id', 'slide-id', 'reveal', 'hidden', 'data-view-transition-type']) copie.removeAttribute(nom);
    for (const a of Array.from(copie.attributes)) {
      if (a.name.indexOf('data-shopify') === 0) copie.removeAttribute(a.name);
    }
    copie.setAttribute('data-boucle-clone', '');
    copie.setAttribute('data-boucle-reel', String(reel));
    copie.setAttribute('aria-hidden', 'true');
    // nom de frise propre à la copie : les animations « au défilement » restent locales
    copie.style.setProperty('--slideshow-timeline', `--boucle-${v < 0 ? 'a' + -v : 'b' + v}`);
    for (const el of Array.from(copie.querySelectorAll('*'))) {
      if (el.id) el.removeAttribute('id');
      for (const a of Array.from(el.attributes)) {
        if (a.name.indexOf('data-shopify') === 0 || a.name === 'data-view-transition-type') el.removeAttribute(a.name);
      }
      if (el.hasAttribute('ref') && !this.#dansAutreComposant(el, copie)) el.removeAttribute('ref');
      if (el.matches('a[href], button, input, select, textarea, iframe, summary, [tabindex], video[controls], audio[controls]')) {
        el.setAttribute('tabindex', '-1');
      }
      if (el.tagName === 'IMG') {
        el.setAttribute('loading', 'lazy');
        el.removeAttribute('fetchpriority');
      }
      // copies au-delà d'un tour (vues seulement pendant un long élan) : pas de vidéo qui se charge ou se lit
      if (el.tagName === 'VIDEO' && (v < -n || v >= 2 * n)) {
        el.removeAttribute('autoplay');
        el.setAttribute('preload', 'none');
      }
    }
    return copie;
  }

  /**
   * @param {Element} el
   * @param {Element} racine
   * @returns {boolean} el appartient à un composant imbriqué dans la copie (ses ref lui reviennent)
   */
  #dansAutreComposant(el, racine) {
    for (let p = el.parentElement; p && p !== racine; p = p.parentElement) {
      if (p instanceof Component || p.tagName.toLowerCase().endsWith('-component')) return true;
    }
    return false;
  }

  /**
   * @returns {{ v: number, el: HTMLElement } | null} la diapositive (vraie ou copie) posée au début de la piste
   */
  #boucleProche() {
    const b = this.#boucle;
    if (!b) return null;
    const { rtl, gauche, droite } = this.#boucleCadre();
    /** @type {{ v: number, el: HTMLElement } | null} */
    let meilleur = null;
    let d = Infinity;
    for (let v = -b.k; v < b.n + b.k; v++) {
      const el = this.#boucleEl(v);
      if (!el) continue;
      const r = el.getBoundingClientRect();
      const e = Math.abs(rtl ? r.right - droite : r.left - gauche);
      if (e < d) {
        d = e;
        meilleur = { v, el };
      }
    }
    return meilleur;
  }

  /** Pas des flèches (diapositives entièrement visibles une fois posées), fixé à la construction. */
  #boucleVisibles() {
    return this.#boucle ? this.#boucle.vis : 1;
  }

  /**
   * Décale la piste instantanément (un tour complet : l'image ne change pas).
   * @param {number} dx - en pixels
   */
  #boucleSauter(dx) {
    const { scroller } = this.refs;
    if (!dx || !this.#scroll) return;
    this.#scroll.to(scroller.scrollLeft + dx, { instant: true });
    this.#noterRepos();
  }

  /**
   * @param {number|string|{id: string}} input
   * @returns {number | null | undefined} indice réel, null (diapositive masquée : mode d'origine), undefined (rien)
   */
  #boucleIndice(input) {
    const slides = this.slides;
    if (!slides) return undefined;
    if (typeof input === 'number') return isNaN(input) ? undefined : input;
    if (typeof input === 'string') {
      const i = parseInt(input, 10);
      return isNaN(i) ? undefined : i;
    }
    if (input && 'id' in input) {
      const demandee = (this.refs.slides || []).find((s) => s.getAttribute('slide-id') == input.id);
      if (!demandee) return undefined;
      if (demandee.hasAttribute('hidden')) return null;
      const i = slides.indexOf(demandee);
      return i < 0 ? undefined : i;
    }
    return undefined;
  }

  /**
   * Glisse par la boucle.
   * @param {number} valeur - pas relatif (flèches, lecture auto) ou indice réel visé (pastilles, miniatures)
   * @param {boolean} relatif
   * @param {Event} [event]
   * @param {{ animate?: boolean }} [options]
   */
  #boucleAller(valeur, relatif, event, options = {}) {
    const b = this.#boucle;
    const slides = this.slides;
    if (!b || !slides || !this.#scroll) return;
    if (event) event.preventDefault();

    const { n, k } = b;
    const instant = prefersReducedMotion() || options.animate === false;
    const bas = -k;
    const haut = n + k - b.vues;

    // où en est la piste (ce qu'on voit), et d'où l'on compte : la cible en cours s'il y en a une
    const proche = this.#boucleProche();
    if (!proche) return;
    const geo = proche.v;
    const base = this.#anime ? this.#vpos : geo;

    let d = valeur;
    if (!relatif) {
      // indice réel : le plus court chemin, par les copies s'il le faut
      d = valeur - this.#mod(base);
      if (d > n / 2) d -= n;
      else if (d < -n / 2) d += n;
    }

    // Destination t (≡ base + d), éventuellement après un saut d'un tour (s) de la piste. La vue actuelle
    // et la destination doivent exister (copies comprises) et l'on continue dans le même sens ; faute de
    // place (clics très rapides), on retire des tours entiers au trajet — même diapositive d'arrivée.
    let voulu = base + d;
    // clics très rapides qui s'accumulent : jamais plus d'un tour de trajet (on ne fait pas défiler
    // toutes les diapositives plusieurs fois) — même diapositive d'arrivée
    if (Math.abs(voulu - geo) > n) voulu -= Math.sign(voulu - geo) * n * Math.ceil((Math.abs(voulu - geo) - n) / n);
    const sens = Math.sign(voulu - geo);
    let t = null;
    let saut = 0;
    // sauts possibles : aucun, un tour, ou le retour dans les vraies diapositives (s0) s'il en faut plusieurs
    const s0 = this.#mod(geo) - geo;
    for (const tours of [0, 1, 2]) {
      for (const s of [0, -n, n, s0, s0 - n, s0 + n]) {
        const essai = voulu - sens * tours * n + s;
        const vue = geo + s;
        const ok = vue >= bas && vue <= haut && essai >= bas && essai <= haut && Math.sign(essai - vue) * sens >= 0;
        if (ok) {
          t = essai;
          saut = s;
          break;
        }
      }
      if (t !== null) break;
    }

    let v = this.#mod(voulu);
    if (t !== null) {
      if (saut) {
        const vers = this.#boucleEl(geo + saut);
        if (vers) {
          // en plein glissement, la piste n'est pas sur un cran : sans cela, le saut serait ré-aimanté
          if (!this.#aimantCoupe) {
            this.#scroll.snap = false;
            this.#aimantCoupe = true;
          }
          this.#boucleSauter(vers.getBoundingClientRect().left - proche.el.getBoundingClientRect().left);
        }
      }
      v = t;
    }

    const el = this.#boucleEl(v);
    if (!el) return;

    const previousIndex = this.#current;
    const index = this.#mod(v);
    const slide = slides[index];
    this.#vpos = v;
    if (!slide) return;

    slide.setAttribute('aria-hidden', 'false');
    this.#anime = !instant;
    this.#animeT = performance.now();
    if (!instant) this.#commandeT = this.#animeT;
    this.#scroll.to(el, { instant });
    if (instant) this.#noterRepos();
    this.current = index;
    this.#centerSelectedThumbnail(index, instant ? 'instant' : 'smooth');

    if (index === previousIndex && d === 0) return;

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: event != null,
        trigger: 'select',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
  }

  /** Piste posée sur une copie : on se replace sur la vraie, d'un tour complet (invisible). */
  #boucleRecentrer = () => {
    const b = this.#boucle;
    const { scroller } = this.refs;
    if (!b || !scroller || !this.#scroll) return;
    if (this.#dragging || this.#doigt || this.hasAttribute('transitioning')) return;
    clearTimeout(this.#minuterieBoucle);
    // glissement commandé pas encore arrivé (départ lent du défilement doux) : on attend la fin
    if (this.#anime) {
      const cible = this.#boucleEl(this.#vpos);
      const { rtl, gauche, droite } = this.#boucleCadre();
      const r = cible ? cible.getBoundingClientRect() : null;
      const ecart = r ? Math.abs(rtl ? r.right - droite : r.left - gauche) : 0;
      if (ecart > 2 && performance.now() - this.#animeT < 1500) {
        this.#minuterieBoucle = setTimeout(this.#boucleRecentrer, 150);
        return;
      }
      this.#anime = false;
    }
    if (this.#aimantCoupe) {
      this.#aimantCoupe = false;
      this.#scroll.snap = true;
    }
    const proche = this.#boucleProche();
    if (!proche) return;
    // un ou plusieurs tours (les copies peuvent dépasser un tour)
    const decal = this.#mod(proche.v) - proche.v;
    if (decal) {
      const jumeau = this.#boucleEl(proche.v + decal);
      if (jumeau) {
        // fin d'élan pas tout à fait posée (image en retard) : un saut maintenant serait ré-aimanté par le
        // navigateur et se verrait ; on attend que la piste soit posée (1,2 s au plus)
        const { rtl, gauche, droite } = this.#boucleCadre();
        const r = proche.el.getBoundingClientRect();
        const posee = Math.abs(rtl ? r.right - droite : r.left - gauche) <= 1;
        const maintenant = performance.now();
        if (!posee) {
          if (!this.#recentrerT) this.#recentrerT = maintenant;
          if (maintenant - this.#recentrerT < 1200) {
            this.#minuterieBoucle = setTimeout(this.#boucleRecentrer, 100);
            return;
          }
        }
        this.#recentrerT = 0;
        // aimantation coupée le temps du saut : même image au pixel près
        this.#scroll.snap = false;
        this.#boucleSauter(jumeau.getBoundingClientRect().left - proche.el.getBoundingClientRect().left);
        this.#scroll.snap = true;
      }
    }
    this.#recentrerT = 0;
    this.#vpos = proche.v + decal;
    const index = this.#mod(proche.v);
    if (index !== this.#current) this.current = index;
    this.#noterRepos();
  };

  get slides() {
    return this.refs.slides?.filter((slide) => !slide.hasAttribute('hidden') || slide.hasAttribute('reveal'));
  }

  /**
   * The initial slide index.
   * @type {number}
   */
  get initialSlideIndex() {
    const initialSlide = this.getAttribute('initial-slide');
    if (initialSlide == null) return 0;

    return parseInt(initialSlide, 10);
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.suspend() : this.resume());

  #updateControlsVisibility() {
    if (!this.hasAttribute('auto-hide-controls')) return;

    const { scroller, slideshowControls } = this.refs;

    if (!(slideshowControls instanceof HTMLElement)) return;

    slideshowControls.hidden = scroller.scrollWidth <= scroller.offsetWidth;
  }

  /**
   * Setup IntersectionObserver for efficient visibility tracking of slides
   */
  #setupIntersectionObserver() {
    const { slides, scroller } = this.refs;
    if (!slides?.length) return;

    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
    }

    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        const allEntries = [
          ...entries,
          ...(this.#intersectionObserver ? this.#intersectionObserver.takeRecords() : []),
        ];

        for (const entry of allEntries) {
          const slide = /** @type {HTMLElement} */ (entry.target);
          const isCurrentlyVisible = this.#visibleSlides.includes(slide);
          const shouldBeVisible = entry.intersectionRatio >= SLIDE_VISIBLITY_THRESHOLD;

          if (shouldBeVisible && !isCurrentlyVisible) {
            this.#visibleSlides.push(slide);
          } else if (!shouldBeVisible && isCurrentlyVisible) {
            const index = this.#visibleSlides.indexOf(slide);
            if (index > -1) {
              this.#visibleSlides.splice(index, 1);
            }
          }
        }

        this.#visibleSlides.sort((a, b) => slides.indexOf(a) - slides.indexOf(b));
        this.#updateVisibleSlides();
      },
      {
        root: scroller,
        threshold: SLIDE_VISIBLITY_THRESHOLD,
        // Add small margin to account for sub-pixel rendering
        rootMargin: '1px',
      }
    );

    // Observe all slides - observer will fire initial callback asynchronously
    slides.forEach((slide) => {
      this.#intersectionObserver?.observe(slide);
    });
  }

  /**
   * Centers the selected thumbnail in the thumbnails container
   * @param {number} index - The index of the selected thumbnail
   * @param {ScrollBehavior} [behavior] - The scroll behavior.
   */
  #centerSelectedThumbnail(index, behavior = 'smooth') {
    const selectedThumbnail = this.refs.thumbnails?.[index];
    if (!selectedThumbnail) return;

    const { thumbnailsContainer } = this.refs;
    if (!thumbnailsContainer || !(thumbnailsContainer instanceof HTMLElement)) return;

    const { slideshowControls } = this.refs;
    if (!slideshowControls || !(slideshowControls instanceof HTMLElement)) return;

    scrollIntoView(selectedThumbnail, {
      ancestor: thumbnailsContainer,
      behavior,
      block: 'center',
      inline: 'center',
    });
  }

  #updateVisibleSlides() {
    const { slides } = this;
    if (!slides || !slides.length) return 0;

    const visibleSlides = this.visibleSlides;

    // If the IntersectionObserver reports zero visible slides, then the slideshow has intersected, but no slides meet the visibility threshold.
    // Probably that the slideshow's host is mid-animation or zero-layout, like in an animating modal.
    // Don't stamp aria-hidden="true" on every slide. Set the current slide to the first visible slide.
    if (visibleSlides.length === 0) return 0;

    // Batch writes to the DOM
    scheduler.schedule(() => {
      // Update aria-hidden based on visibility
      slides.forEach((slide) => {
        const isVisible = visibleSlides.includes(slide);
        slide.setAttribute('aria-hidden', `${!isVisible}`);
      });
    });

    return visibleSlides.length;
  }
}

if (!customElements.get('slideshow-component')) {
  customElements.define('slideshow-component', Slideshow);
}
